// Fixed, scroll-driven parallax starfield. Each layer drifts at its own depth as the page scrolls.
class StarfieldElement extends HTMLElement {
  #canvas = null;
  #ctx = null;
  #layers = [];
  #frame = 0;
  #abort = null;
  #reducedMotion = false;
  #width = 0;
  #height = 0;
  #dpr = 1;

  connectedCallback() {
    this.#canvas = this.querySelector('canvas');
    if (!this.#canvas) return;
    this.#ctx = this.#canvas.getContext('2d');
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#reducedMotion = motionQuery.matches;
    motionQuery.addEventListener('change', (event) => {
      this.#reducedMotion = event.matches;
      this.#schedule();
    }, { signal });

    window.addEventListener('scroll', () => this.#schedule(), { passive: true, signal });
    window.addEventListener('resize', () => this.#resize(), { passive: true, signal });

    this.#resize();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #resize() {
    this.#dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.#width = window.innerWidth;
    this.#height = window.innerHeight;
    this.#canvas.width = Math.round(this.#width * this.#dpr);
    this.#canvas.height = Math.round(this.#height * this.#dpr);
    this.#canvas.style.width = `${this.#width}px`;
    this.#canvas.style.height = `${this.#height}px`;
    this.#seed();
    this.#schedule();
  }

  #seed() {
    const density = Number(this.dataset.density || 60) / 100;
    const area = (this.#width * this.#height) / 10000;
    const strength = Number(this.dataset.speed || 50) / 50;
    const styles = getComputedStyle(this);
    const starColor = styles.getPropertyValue('--color-on-surface').trim() || '#ffffff';
    const accentColor = styles.getPropertyValue('--color-accent').trim() || starColor;

    // Far, mid and near layers: smaller and slower in the back, brighter and faster up front.
    const recipes = [
      { count: area * 2.2, size: [0.4, 0.9], alpha: [0.25, 0.55], depth: 0.08 },
      { count: area * 0.9, size: [0.8, 1.4], alpha: [0.45, 0.8], depth: 0.22 },
      { count: area * 0.25, size: [1.3, 2.2], alpha: [0.7, 1], depth: 0.5 },
    ];

    this.#layers = recipes.map((recipe) => {
      const stars = [];
      const total = Math.max(4, Math.round(recipe.count * density));
      for (let i = 0; i < total; i++) {
        stars.push({
          x: Math.random() * this.#width,
          y: Math.random() * this.#height,
          r: recipe.size[0] + Math.random() * (recipe.size[1] - recipe.size[0]),
          a: recipe.alpha[0] + Math.random() * (recipe.alpha[1] - recipe.alpha[0]),
          tint: Math.random() < 0.12 ? accentColor : starColor,
        });
      }
      return { stars, depth: recipe.depth * strength };
    });
  }

  #schedule() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#draw());
  }

  #draw() {
    const ctx = this.#ctx;
    const h = this.#height;
    const scroll = this.#reducedMotion ? 0 : window.scrollY;

    ctx.setTransform(this.#dpr, 0, 0, this.#dpr, 0, 0);
    ctx.clearRect(0, 0, this.#width, h);

    for (const layer of this.#layers) {
      const offset = scroll * layer.depth;
      for (const star of layer.stars) {
        // Wrap vertically so the field never runs out, however far the page scrolls.
        const y = (((star.y - offset) % h) + h) % h;
        ctx.globalAlpha = star.a;
        ctx.fillStyle = star.tint;
        ctx.beginPath();
        ctx.arc(star.x, y, star.r, 0, Math.PI * 2);
        ctx.fill();
        if (star.r > 1.6) {
          ctx.globalAlpha = star.a * 0.18;
          ctx.beginPath();
          ctx.arc(star.x, y, star.r * 3, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
  }
}

if (!customElements.get('s-starfield')) {
  customElements.define('s-starfield', StarfieldElement);
}
